import { type BuilderInfo } from "helpers/AppProvider.types";

import { CostType, MediaType, OpenActivityType } from "types/enum";

import { jobDetailRoute } from "utilities/flatRoutes/job.routes";
import { leadDetailRoute } from "utilities/flatRoutes/lead.routes";
import { getBaseRoute, routes } from "utilities/routes";

import type { ISearchHit } from "commonComponents/utilities/MainNavigation/SearchDialog/search/search.types";

import { normalizeKey } from "./entityMeta";

/**
 * Navigation strategy for a search result link.
 *
 * - "relative" — opens as a modal overlay via SearchBarRoutes.
 *   Path includes the /SearchResult prefix and is pushed relative to match.url.
 *   Matches the RelativeLink behaviour in the legacy GlobalSearch.
 *
 * - "direct" — navigates to the entity's full page (AutoSPARouting).
 *   Used for entities that the legacy GlobalSearch routed directly.
 *
 * - "external" — a help-center article the agent cited, opened in a new tab.
 */
type NavType = "relative" | "direct" | "external";

interface IEntityLink {
    path: string;
    navType: NavType;
}

function relativeLink(path: string): IEntityLink {
    return { path: `${routes.search.resultModalPrefix}${path}`, navType: "relative" };
}

function directLink(path: string): IEntityLink {
    return { path, navType: "direct" };
}

const HELP_CENTER_HOST = "helpcenter.buildertrend.net";

/**
 * The agent's help tool cites a help-center section with its public URL in the
 * payload. Only an https link on the help center itself is followed, so a
 * payload can never send the user anywhere else.
 */
function helpArticleLink(url: unknown): IEntityLink | null {
    if (typeof url !== "string") return null;
    try {
        const parsed = new URL(url);
        if (parsed.protocol !== "https:" || parsed.hostname !== HELP_CENTER_HOST) return null;
        return { path: parsed.href, navType: "external" };
    } catch {
        return null;
    }
}

/** Maps the search index's media type code to the app's MediaType; defaults to Document. */
function getMediaTypeFromPayload(value: unknown): MediaType {
    const code = Number(value) as MediaType;
    return code === MediaType.Photo || code === MediaType.Video ? code : MediaType.Document;
}

/**
 * Build a navigation link for a search result entity.
 *
 * Mirrors the legacy GlobalSearch link-type assignments:
 *   - RelativeLink entities open as modals via SearchBarRoutes
 *   - AutoSPARouting entities navigate directly to their detail page
 *   - Entities not in legacy search default to AutoSPARouting if functioning
 */
function getEntityLink(hit: ISearchHit, builderInfo?: BuilderInfo): IEntityLink | null {
    const jobId =
        Number(hit.payload?.jobId ?? hit.payload?.job_id ?? hit.payload?.jobsite_id) || null;
    const entity = normalizeKey(hit.entity);

    // Help sections are not records: string ids, and the link is the payload's URL.
    if (entity === "helparticle") return helpArticleLink(hit.payload?.url);

    const id = Number(hit.id);
    if (isNaN(id)) return null;

    // ── Non-job-scoped entities ─────────────────────────────────────────
    switch (entity) {
        case "contact":
            return relativeLink(routes.contact.getDetailsLink(id, false));
        case "lead":
            if (builderInfo?.flags.leadsFullPageConversion) {
                return directLink(
                    leadDetailRoute.link({ leadId: id }, undefined, {
                        usePrevious: false,
                    }) as string,
                );
            }
            return relativeLink(routes.lead.getDetailsLink(id));
        case "sub":
            return relativeLink(routes.sub.getDetailsLink(id));
        case "internaluser":
            return relativeLink(routes.internalUsers.getInternalUserDetailsLink(id));
        case "email":
        case "emailmessage":
            return relativeLink(routes.messages.getMessageDetailsLink(id));

        case "job":
        case "jobsite":
            return directLink(
                jobDetailRoute.link({ jobId: id }, undefined, { usePrevious: false }) as string,
            );

        case "task": {
            const externalId = hit.payload?.externalId;
            if (typeof externalId !== "string" || !externalId) return null;
            return relativeLink(routes.task.getDetailsLink(externalId));
        }

        case "costcode":
            return relativeLink(routes.costCode.getCostCodeDetailsLink(id, CostType.Both));

        case "leadactivity": {
            const leadId = Number(hit.payload?.leadId ?? hit.payload?.lead_id);
            if (isNaN(leadId)) return null;
            return relativeLink(
                routes.leadActivity.getDetailsLink(id, leadId, OpenActivityType.Edit, undefined),
            );
        }
    }

    // ── Job-scoped entities ─────────────────────────────────────────────
    if (!jobId) return null;

    switch (entity) {
        case "schedule":
        case "scheduleitem":
            return relativeLink(routes.schedule.getDetailsLink(id, jobId));
        case "purchaseorder":
            return relativeLink(routes.purchaseOrder.getDetailsLink(id, jobId));
        case "selection":
            return relativeLink(routes.selection.getDetailsLink(id, jobId));
        case "allowance":
            return relativeLink(routes.allowance.getDetailsLink(id, jobId));
        case "bid":
            return relativeLink(routes.bid.getDetailsLink(id, jobId));
        case "bidpackage":
            return relativeLink(routes.bidPackage.getDetailsLink(id, jobId));
        case "bill":
            return relativeLink(routes.bill.getDetailsLink(id, jobId));
        case "warranty":
            if (builderInfo?.flags.warrantiesModalConversion) {
                return directLink(routes.warranty.getDetailsLink(id, jobId));
            }
            return relativeLink(routes.warranty.getDetailsLink(id, jobId));
        case "rfi":
            return relativeLink(routes.rfi.getDetailsLink(id, jobId));
        case "ownerinvoice":
        case "customerinvoice":
            return relativeLink(routes.ownerInvoice.getDetailsLink(id, jobId));
        case "document":
            // Photos and videos are indexed as documents; the media type in the
            // payload picks the matching properties page.
            return relativeLink(
                routes.media.getMediaPropertiesLink(
                    id,
                    getMediaTypeFromPayload(hit.payload?.mediaType),
                    jobId,
                ),
            );
        case "proposal":
            // Opens the lead page with the hit id, same as the modal route below does today.
            if (builderInfo?.flags.leadsFullPageConversion) {
                return directLink(
                    leadDetailRoute.link({ leadId: id }, undefined, {
                        usePrevious: false,
                    }) as string,
                );
            }
            return relativeLink(routes.lead.getDetailsLink(id));

        // AutoSPARouting in legacy search
        case "changeorder":
            return directLink(routes.changeOrder.getChangeOrderDetailsFullPage(id, jobId));
        case "dailylog":
            return directLink(routes.dailyLog.getDetailsLink(id, jobId));

        case "selectionchoice":
            return relativeLink(routes.selectionChoice.getDetailsLink(id, jobId));
        case "deposit":
            return relativeLink(routes.deposits.getDetailsLink(id, jobId));
        case "creditmemo":
            return relativeLink(routes.creditMemos.getDetailsLink(id, jobId));
        case "submittal": {
            const externalId = hit.payload?.externalId;
            if (typeof externalId !== "string" || !externalId) return null;
            return directLink(routes.submittal.getDetailsLink(externalId, jobId));
        }
        default:
            return null;
    }
}

function navigateEntityLink(
    link: IEntityLink,
    history: { push: (path: string) => void },
    matchUrl: string,
    isSpa: boolean,
) {
    if (link.navType === "external") {
        window.open(link.path, "_blank", "noopener,noreferrer");
    } else if (link.navType === "relative") {
        history.push(matchUrl.replace(/\/$/, "") + link.path);
    } else if (isSpa) {
        history.push(link.path);
    } else {
        window.location.assign(getBaseRoute() + link.path);
    }
}

export { getEntityLink, navigateEntityLink };
